import { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { getAdminCODN, deleteCODN, downloadCODNPDF } from "@/lib/api";
import { Trash2, Loader2, Calculator, Eye, IndianRupee, Download } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const PILLAR_LABELS = {
  manual_entry_cost: "Manual Data Entry", reconciliation_cost: "Reconciliation",
  error_cost: "Errors & Rework", reporting_cost: "Reporting Delays",
  inventory_cost: "Inventory Waste", compliance_cost: "Compliance Risk"
};

export default function AdminCODN() {
  const [calculations, setCalculations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try { const res = await getAdminCODN(); setCalculations(res.data); }
    catch { toast.error("Failed to load CODN data"); }
    finally { setLoading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this calculation?")) return;
    try { await deleteCODN(id); setCalculations(c => c.filter(x => x.id !== id)); toast.success("Deleted"); }
    catch { toast.error("Failed to delete"); }
  };

  const fmtRupee = (v) => {
    if (!v) return "Rs.0";
    if (v >= 10000000) return `Rs.${(v/10000000).toFixed(1)} Cr`;
    if (v >= 100000) return `Rs.${(v/100000).toFixed(1)}L`;
    return `Rs.${v.toLocaleString("en-IN")}`;
  };

  const handleDownloadPDF = async (c) => {
    try {
      toast.info("Generating PDF...");
      const res = await downloadCODNPDF(c.id);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `CODN_${c.company_name.replace(/\s+/g, '_')}_${c.id.slice(0, 8)}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded!");
    } catch { toast.error("Failed to download PDF"); }
  };

  const totalAllCODN = calculations.reduce((s, c) => s + (c.total_codn || 0), 0);
  const avgCODN = calculations.length > 0 ? totalAllCODN / calculations.length : 0;

  return (
    <AdminLayout title="CODN Calculator Submissions">
      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        <Card><CardContent className="p-4 text-center"><div className="text-2xl font-bold text-red-600">{calculations.length}</div><div className="text-xs text-slate-500">Total Calculations</div></CardContent></Card>
        <Card><CardContent className="p-4 text-center"><div className="text-2xl font-bold text-amber-600">{fmtRupee(avgCODN)}</div><div className="text-xs text-slate-500">Avg Annual Loss</div></CardContent></Card>
        <Card><CardContent className="p-4 text-center"><div className="text-2xl font-bold text-[#0066CC]">{calculations.filter(c => c.total_codn >= 2000000).length}</div><div className="text-xs text-slate-500">High-value Leads (20L+)</div></CardContent></Card>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></div>
          ) : calculations.length === 0 ? (
            <div className="p-8 text-center text-slate-400"><Calculator className="w-8 h-8 mx-auto mb-2" />No calculations yet</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Company</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Industry</TableHead>
                  <TableHead>Annual Loss</TableHead>
                  <TableHead>Daily Loss</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {calculations.map(c => (
                  <TableRow key={c.id}>
                    <TableCell className="font-medium">{c.company_name}</TableCell>
                    <TableCell><div className="text-sm">{c.contact_name}</div><div className="text-xs text-slate-400">{c.contact_email}</div></TableCell>
                    <TableCell className="text-sm">{c.industry || "-"}</TableCell>
                    <TableCell><span className="font-bold text-red-600">{fmtRupee(c.total_codn)}</span></TableCell>
                    <TableCell className="text-sm">{fmtRupee(c.daily_loss)}/day</TableCell>
                    <TableCell className="text-xs text-slate-500">{new Date(c.created_at).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => setSelected(c)}><Eye className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDownloadPDF(c)} data-testid={`download-codn-pdf-${c.id}`} title="Download PDF"><Download className="w-4 h-4 text-[#0066CC]" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(c.id)} className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{selected?.company_name} — CODN Report</DialogTitle></DialogHeader>
          {selected && (
            <div className="space-y-4">
              <div className="text-center p-4 bg-red-50 rounded-lg">
                <div className="flex items-center justify-center gap-1">
                  <IndianRupee className="w-6 h-6 text-red-600" />
                  <span className="text-3xl font-bold text-red-600">{fmtRupee(selected.total_codn)}</span>
                </div>
                <div className="text-sm text-slate-600">{fmtRupee(selected.daily_loss)}/day</div>
              </div>
              <div className="space-y-2">
                {Object.entries(selected.results || {}).filter(([k]) => k !== "total_codn").map(([k, v]) => (
                  <div key={k} className="flex justify-between p-3 bg-slate-50 rounded">
                    <span className="text-sm">{PILLAR_LABELS[k] || k}</span>
                    <span className="font-bold text-sm">{fmtRupee(v)}</span>
                  </div>
                ))}
              </div>
              {selected.impact_statements?.length > 0 && (
                <div className="p-3 bg-amber-50 rounded border border-amber-100">
                  <p className="text-xs font-semibold text-amber-700 mb-2">Impact Analysis:</p>
                  {selected.impact_statements.map((s, i) => (
                    <p key={`impact-${i}-${s.slice(0, 20)}`} className="text-xs text-amber-800 mb-1">- {s}</p>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
